/**
 * Growth KPI Utilities — Event-based approach
 *
 * Extracts completed action items & reports as a flat event list,
 * sorted by date, then groups nearby events into clusters.
 */

const CLUSTER_WINDOW_DAYS = 3;

/**
 * Normalise date to YYYY-MM-DD string.
 */
const toDateKey = (dateStr) => {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Extract all completion events from action items and reports.
 * Returns sorted array of { id, type: 'action'|'report', title, date: Date, dateKey }.
 */
export const extractEvents = (actionItems = [], reports = []) => {
  const events = [];

  actionItems.forEach(item => {
    if (item.status === 3 && !item._deleted) {
      events.push({
        id: item.id,
        type: 'action',
        title: item.title || 'Action Item',
        date: new Date(item.updatedAt),
        dateKey: toDateKey(item.updatedAt),
      });
    }
  });

  reports.forEach(report => {
    if (report.completed && !report._deleted) {
      events.push({
        id: report.id,
        type: 'report',
        title: report.name || 'Report',
        date: new Date(report.updatedAt),
        dateKey: toDateKey(report.updatedAt),
      });
    }
  });

  return events.sort((a, b) => a.date - b.date);
};

/**
 * Filter events to a date range.
 */
export const filterEventsByRange = (events, start, end) => {
  const s = new Date(start); s.setHours(0, 0, 0, 0);
  const e = new Date(end); e.setHours(23, 59, 59, 999);
  return events.filter(ev => ev.date >= s && ev.date <= e);
};

/**
 * Simple cluster algorithm — exactly as user specified.
 * Walk through sorted events; if next event is within CLUSTER_WINDOW_DAYS of
 * previous, add to current cluster. Otherwise flush and start new cluster.
 * Only groups of 2+ become clusters.
 *
 * Returns { clusters: [...], clusterEventIds: Set }
 * Each cluster: { events[], startDate, endDate, actionCount, reportCount, spanDays, label, markerDate }
 */
export const findClusters = (events, windowDays = CLUSTER_WINDOW_DAYS) => {
  if (events.length === 0) return { clusters: [], clusterEventIds: new Set() };

  const sorted = [...events].sort((a, b) => a.date - b.date);
  const clusters = [];
  let currentCluster = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    const diffDays = (curr.date - prev.date) / (24 * 60 * 60 * 1000);

    if (diffDays <= windowDays) {
      currentCluster.push(curr);
    } else {
      if (currentCluster.length > 1) {
        clusters.push(buildCluster(currentCluster));
      }
      currentCluster = [curr];
    }
  }
  // Catch last cluster
  if (currentCluster.length > 1) {
    clusters.push(buildCluster(currentCluster));
  }

  const clusterEventIds = new Set();
  clusters.forEach(c => c.events.forEach(e => clusterEventIds.add(e.id)));

  return { clusters, clusterEventIds };
};

function buildCluster(events) {
  const startDate = events[0].date;
  const endDate = events[events.length - 1].date;
  const actionCount = events.filter(e => e.type === 'action').length;
  const reportCount = events.filter(e => e.type === 'report').length;
  const spanDays = Math.max(1, Math.ceil((endDate - startDate) / (24 * 60 * 60 * 1000)));

  const parts = [];
  if (actionCount > 0) parts.push(`${actionCount} action${actionCount !== 1 ? 's' : ''}`);
  if (reportCount > 0) parts.push(`${reportCount} report${reportCount !== 1 ? 's' : ''}`);

  return {
    events,
    startDate,
    endDate,
    actionCount,
    reportCount,
    spanDays,
    label: `${parts.join(' + ')} in ${spanDays} day${spanDays !== 1 ? 's' : ''}`,
    // Position cluster marker at the last event date (simplest, as user specified)
    markerDate: endDate,
    markerDateKey: toDateKey(endDate),
  };
}

/**
 * Filter already-detected clusters to those whose marker date (last event
 * in the cluster) falls within the range. Clustering must run on the full
 * event history BEFORE this filter — filtering events first and clustering
 * second drops any cluster whose earlier events fall outside the range.
 */
export const filterClustersByRange = (clusters, start, end) => {
  const s = new Date(start); s.setHours(0, 0, 0, 0);
  const e = new Date(end); e.setHours(23, 59, 59, 999);
  return clusters.filter(c => c.markerDate >= s && c.markerDate <= e);
};

/**
 * Compute summary counts.
 */
export const computeSummary = (events, clusters) => ({
  actionItems: events.filter(e => e.type === 'action').length,
  reports: events.filter(e => e.type === 'report').length,
  clusters: clusters.length,
});

/**
 * Preset date ranges.
 */
export const getDateRangePresets = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return {
    'Last 30 Days': {
      start: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30),
      end: new Date(),
    },
    'Last 90 Days': {
      start: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 90),
      end: new Date(),
    },
    'Last 6 Months': {
      start: new Date(today.getFullYear(), today.getMonth() - 6, today.getDate()),
      end: new Date(),
    },
    'Year to Date': {
      start: new Date(today.getFullYear(), 0, 1),
      end: new Date(),
    },
    'Last 12 Months': {
      start: new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()),
      end: new Date(),
    },
  };
};
